<template lang="pug">
#other-user-safety-confirm-template-edit(v-cloak)
  .box
    .box-header.with-border.register-header
      .title
        span 安否確認（施設関係者）の事前設定
    .box-body.register-body
      .note
        span 宛先・案内内容・質問項目を事前に設定することができます（質問内容は事前設定が必須です）。
        br
        span 宛先用の宛先グループは、利用者等管理の宛先グループで作成してください。

      TargetSelectButton(v-model="groups" :isBusy="isBusy" @onTargetBtn="onTargetBtn")

      FormTextarea(
        key-name="メッセージ内容"
        name="text"
        v-model="openingSentence"
        placeholder="施設関係者に送るメッセージ内容を入力してください。\n\n例：\n安否確認のテストを実施します。\nみなさんの仮の安否情報を入力してください。"
        :validation-rules="{ max: 300 }"
        :maxlength="300"
        :styles="{ maxWidth: '760px', width: '760px', minHeight: '160px' }")

      .confirm-list.d-flex.flex-column.ml-2
        span.questions.ml-2 質問項目
        .content.d-flex.flex-row.align-items-center(v-for="item in confirms")
          .confirm.flex-grow-1
            .title {{ item.label }}
            .text {{ item.text }}
            .text(v-if="item.inputType == 0") 選択肢：{{ item.choicesLabel }}
            .text(v-else-if="item.inputType == 1") 自由記述
          .buttons.d-flex.flex-row-reverse
            PermittedFunctionSwitch(v-model="item.use")

      .register-row(v-show="errorMessage")
        .register-group
          .register-value
            .error-message {{ errorMessage }}

    .box-footer.with-border.register-footer
      .buttons
        button.btn.update-btn(@click="previewConfirm" type="button") プレビュー
        button.btn.update-btn(@click="showConfirmUpdateModal" type="button") 更新
        button.btn.cancel-btn(@click="gotoSafetyConfirmList" type="button") キャンセル

  PreviewOtherUserTemplateModal(v-model="showPreviewModal" :confirms="confirms")

  GroupSelectModal(ref="groupSelectModal" :groupType.sync="groupType" @add="addSendGroupTarget")

  OkCancelModal(v-model="showUpdateModal" title="安否確認（施設関係者）の事前設定を更新しますか？" @ok="doUpdate")
  OkModal(v-model="showUpdateSuccessModal" title="更新に成功しました。" @ok="gotoSafetyConfirmList")

  PageLoading(:isBusy="isBusy" :message="loadingMessage")
</template>

<script lang="ts">
import { Component, Inject, Vue } from "vue-property-decorator";
import { userStore } from "@/store/store";

import { PcUrl } from "@/commons/constants/PcUrl";
import SystemError from "@/commons/models/SystemError";
import OtherUserSafetyConfirmsTemplatesService, {
  OtherUserSafetyConfirmsTemplatesGetModel,
  OtherUserSafetyConfirmsTemplatesPatchModel
} from "@/commons/services/OtherUserSafetyConfirmsTemplates";
import SendMessageGroupsService, { GroupType, SendMessageGroupsGetModel } from "@/commons/services/SendMessageGroups";
import OtherUserSafetyConfirmMastersService, {
  OtherUserSafetyConfirmMastersGetModel,
  OtherUserSafetyConfirmMasterRadioInputOption
} from "@/commons/services/OtherUserSafetyConfirmMasters";
import OtherUserGroup from "@/commons/models/OtherUserGroup";
import OtherUserGroupsService from "@/commons/services/OtherUserGroups";
import OtherUserGroupMembersService from "@/commons/services/OtherUserGroupMembers";
import SendMessageGroupMembersService from "@/commons/services/SendMessageGroupMembers";

import OkCancelModal from "@/pc/basics/OkCancelModal.vue";
import OkModal from "@/pc/basics/OkModal.vue";
import PageLoading from "@/pc/basics/PageLoading.vue";
import FormInputSelect from "@/pc/components/forms/FormInputSelect.vue";
import FormTextarea from "@/pc/components/forms/FormTextarea.vue";
import PermittedFunctionSwitch from "@/pc/basics/PermittedFunctionSwitch.vue";
import PreviewOtherUserTemplateModal from "@/pc/containers/safetyConfirms/PreviewOtherUserTemplateModal.vue";
import TargetSelectButton, { GroupModel, OtherUserGroupTargetModel, SendMessageGroupTargetModel } from "@/pc/containers/safetyConfirms/TargetSelectButton.vue";
import GroupSelectModal from "@/pc/containers/safetyConfirms/GroupSelectModal.vue";

/**
 * 使用機能の確認画面コンポーネント
 */
@Component({
  components: {
    OkCancelModal,
    OkModal,
    PreviewOtherUserTemplateModal,
    FormInputSelect,
    FormTextarea,
    PermittedFunctionSwitch,
    PageLoading,
    TargetSelectButton,
    GroupSelectModal
  }
})
export default class OtherUserTemplateEdit extends Vue {
  /** 安否確認(施設関係者)テンプレートサービス */
  @Inject("OtherUserSafetyConfirmsTemplatesService") otherUserSafetyConfirmsTemplatesService!: OtherUserSafetyConfirmsTemplatesService;
  /** 安否確認(施設関係者)で送信する質問内容のマスタサービス */
  @Inject("OtherUserSafetyConfirmMastersService") otherUserSafetyConfirmMastersService!: OtherUserSafetyConfirmMastersService;
  /** 宛先グループサービス */
  @Inject("SendMessageGroupsService") sendMessageGroupsService!: SendMessageGroupsService;
  /** 宛先グループメンバーサービス */
  @Inject("SendMessageGroupMembersService") sendMessageGroupMembersService!: SendMessageGroupMembersService;
  /** 施設関係者グループサービス */
  @Inject("OtherUserGroupsService") otherUserGroupsService!: OtherUserGroupsService;
  /** 施設関係者グループメンバー取得サービス */
  @Inject("OtherUserGroupMembersService") otherUserGroupMembersService!: OtherUserGroupMembersService;

  /** ページ全体ローディング表示可否 */
  isBusy: boolean = false;
  /** ローディングメッセージ */
  loadingMessage: string = "";
  /** 更新確認モーダル表示可否 */
  showUpdateModal: boolean = false;
  /** 更新完了モーダル表示可否 */
  showUpdateSuccessModal: boolean = false;

  /** プレビューモーダル表示可否 */
  showPreviewModal: boolean = false;

  /** 安否確認(施設関係者)で送信する質問内容のマスタ */
  masters: Array<OtherUserSafetyConfirmMastersGetModel> = [];
  /** 安否確認(施設関係者)テンプレート */
  template: OtherUserSafetyConfirmsTemplatesGetModel | null = null;

  /** グループ */
  groups: GroupModel = {
    familyGroups: [],
    otherUserGroups: [],
    residentGroups: [],
    otherUserGroupMemberGroups: []
  };
  /** グループ種別 */
  groupType: GroupType = GroupType.UNKNOWN;
  /** 施設関係者グループメンバーのMAP。key：施設関係者グループID、value：施設関係者グループの配列 */
  otherUserGroupMemberMap: Map<number, Array<OtherUserGroup>> = new Map();
  /** 冒頭文 */
  openingSentence: string = "";
  /** 質問リスト */
  confirms: Array<UseId> = [];

  /** エラーメッセージ */
  errorMessage: string = "";

  /**
   * ### vue lifecycle mounted
   */
  public async mounted(): Promise<void> {
    await this.createView();

    return;
  }

  /**
   * ### 画面情報作成
   *
   * - 更新対象の安否確認(施設関係者)テンプレートを取得する
   * - テンプレートの使用する質問のMAPを作る
   * - 宛先グループを取得する
   * - 取得したグループから宛先グループ選択肢を作成する
   * - 安否確認(施設関係者)で送信する質問内容のマスタを取得する
   * - 質問のMAP、質問内容のマスタから質問の使用する/しないの入力欄を作成する
   */
  public async createView(): Promise<void> {
    this.isBusy = true;
    this.loadingMessage = "読込み中...";

    // 宛先選択肢のグループ情報を作成する
    const sendMessageGroups: Array<SendMessageGroupsGetModel> = await this.sendMessageGroupsService.gets(userStore);
    const otherUserGroups: Array<OtherUserGroup> = await this.otherUserGroupsService.gets(userStore);
    const otherUserGroupMembersInFacility: Array<OtherUserGroup> = await this.otherUserGroupMembersService.get(userStore);
    const otherUserGroupMemberMap: Map<number, Array<OtherUserGroup>> = this.createOtherUserGroupMemberMap(otherUserGroupMembersInFacility);
    this.groups = await this.createTargetGroups(sendMessageGroups, otherUserGroups, otherUserGroupMemberMap);

    const template: OtherUserSafetyConfirmsTemplatesGetModel = await this.getTemplate();
    const useIdMap: Map<number, boolean> = this.createUseIdMap(template);
    this.template = template;
    // 宛先情報の復元は分岐が必要
    // v3.33.0以前のバージョンは宛先グループのgroupId（単体）を保持している
    // v3.34.0以降のバージョンは宛先情報（複数）を保持している
    // 宛先情報（複数）が存在する場合はv3.34.0以降のデータと判断し、存在せずgroupIdが存在する場合はv3.33.0以前のデータと判断する
    if (template.targets) {
      this.restoreTargetsByLatest(template.targets, this.groups);
    } else if (template.groupId != null) {
      this.restoreTargetsByOld(template, this.groups);
    }
    this.openingSentence = template.openingSentence ? template.openingSentence : "";

    const masters: Array<OtherUserSafetyConfirmMastersGetModel> = await this.getOtherUserSafetyConfirmMasters();
    this.masters = masters;

    const confirms: Array<UseId> = this.createConfirms(masters, useIdMap);
    this.confirms = confirms;

    this.isBusy = false;

    return;
  }

  private createOtherUserGroupMemberMap(otherUserGroupMembersInFacility: Array<OtherUserGroup>): Map<number, Array<OtherUserGroup>> {
    const map: Map<number, Array<OtherUserGroup>> = new Map<number, Array<OtherUserGroup>>();
    for (const member of otherUserGroupMembersInFacility) {
      const members: Array<OtherUserGroup> | undefined = map.get(member.id);
      if (members) {
        members.push(member);
        map.set(member.id, members);
      } else {
        map.set(member.id, [member]);
      }
    }
    return map;
  }

  /**
   * ### 宛先のグループ情報を作成する
   */
  private async createTargetGroups(
    sendMessageGroups: Array<SendMessageGroupsGetModel>,
    otherUserGroups: Array<OtherUserGroup>,
    otherUserGroupMemberMap: Map<number, Array<OtherUserGroup>>
  ): Promise<GroupModel> {
    // 宛先グループの情報作成
    const targetOtherUserGroupMemberGroups: Array<SendMessageGroupTargetModel> = [];
    for (const group of sendMessageGroups) {
      const _group: SendMessageGroupTargetModel = Object.assign({}, group) as SendMessageGroupTargetModel;
      _group.isTarget = false;
      _group.memberCount = group.members.length;
      if (_group.type == GroupType.OTHER_USER) {
        targetOtherUserGroupMemberGroups.push({
          ..._group,
          show: true
        });
      }
    }
    // 施設関係者グループの情報を作成
    const targetOtherUserGroups: Array<OtherUserGroupTargetModel> = [];
    for (const group of otherUserGroups) {
      const _group: OtherUserGroupTargetModel = Object.assign({}, group) as OtherUserGroupTargetModel;
      _group.isTarget = false;
      const members: Array<OtherUserGroup> | undefined = otherUserGroupMemberMap.get(_group.id);
      _group.memberCount = members ? members.length : 0;
      targetOtherUserGroups.push({
        ..._group,
        key: `otherUserGroup_${_group.id}`,
        show: true
      });
    }

    return {
      familyGroups: [],
      otherUserGroupMemberGroups: targetOtherUserGroupMemberGroups,
      residentGroups: [],
      otherUserGroups: targetOtherUserGroups
    };
  }

  /**
   * ### 安否確認(施設関係者)テンプレートを取得する
   *
   * - 現時点ではテンプレートは1件のみなのでid指定は1固定
   */
  public async getTemplate(): Promise<OtherUserSafetyConfirmsTemplatesGetModel> {
    const response: OtherUserSafetyConfirmsTemplatesGetModel = await this.otherUserSafetyConfirmsTemplatesService.get(userStore, 1);
    return response;
  }

  /**
   * ### テンプレートの使用する質問のMAPを作る
   *
   * - テンプレートの使用するIDリストに格納されている質問のみ使用する
   */
  public createUseIdMap(template: OtherUserSafetyConfirmsTemplatesGetModel): Map<number, boolean> {
    let map: Map<number, boolean> = new Map<number, boolean>();

    for (const useId of template.useIds) {
      map.set(useId, true);
    }

    return map;
  }

  /**
   * ### 宛先を復元する（v3.33.0以前）
   *
   * - 宛先グループIDで検索して一致するものを宛先対象とする
   * - v3.33.0以前は宛先グループ（施設関係者）しか宛先に設定できなかったの検索対象はotherUserGroupMemberGroupsのみ
   */
  private restoreTargetsByOld(template: OtherUserSafetyConfirmsTemplatesGetModel, groups: GroupModel): void {
    for (const group of groups.otherUserGroupMemberGroups) {
      if (group.id == template.groupId) {
        group.isTarget = true;
        return;
      }
    }
    return;
  }

  /**
   * ### 宛先を復元する（v3.34.0以降）
   *
   * - ソートキーリストで検索して一致するものを宛先対象とする
   * - 宛先グループ（施設関係者）、施設関係者グループのみが対象となる
   */
  private restoreTargetsByLatest(targets: string[], groups: GroupModel): void {
    // 検索用にmapにする
    const map: Map<string, string> = new Map<string, string>();
    for (const target of targets) {
      map.set(target, target);
    }

    for (const group of groups.otherUserGroupMemberGroups) {
      if (map.has(String(group.id))) {
        group.isTarget = true;
      }
    }
    for (const group of groups.otherUserGroups) {
      if (map.has(group.otherUserGroupsSortKey)) {
        group.isTarget = true;
      }
    }
    return;
  }

  /**
   * ### 安否確認(施設関係者)で送信する質問内容のマスタを取得する
   */
  public async getOtherUserSafetyConfirmMasters(): Promise<Array<OtherUserSafetyConfirmMastersGetModel>> {
    const response: Array<OtherUserSafetyConfirmMastersGetModel> = await this.otherUserSafetyConfirmMastersService.get(userStore);
    return response;
  }

  /**
   * ### 入力させる使用する質問リストを作成する
   *
   * - 入力種別がラジオボタンの質問は選択肢ラベルを作成する
   * - テンプレート情報から質問を使用するかしないかを設定する
   */
  public createConfirms(masters: Array<OtherUserSafetyConfirmMastersGetModel>, useIdMap: Map<number, boolean>): Array<UseId> {
    let confirms: Array<UseId> = [];

    for (const master of masters) {
      let choicesLabel: string = "";
      if (master.inputType == 0) {
        const options: OtherUserSafetyConfirmMasterRadioInputOption = master.inputOptions as OtherUserSafetyConfirmMasterRadioInputOption;
        for (const choice of options.choices) {
          choicesLabel += "／" + choice.label;
        }
        // 先頭に「／」が入っているので、消す
        choicesLabel = choicesLabel.slice(1);
      }

      const use: boolean | undefined = useIdMap.get(master.id);
      const useId: UseId = {
        ...master,
        choicesLabel: choicesLabel,
        use: use == true ? true : false
      };
      confirms.push(useId);
    }

    return confirms;
  }

  /**
   * ### 宛先モーダルを表示する
   */
  onTargetBtn(): void {
    // メモリ渡しだと値変更が共有されてしまう
    // 値変更後に「キャンセル」された場合に困るので、コピーしたものを渡す
    const copy: GroupModel = JSON.parse(JSON.stringify(this.groups));
    const modal: any = this.$refs.groupSelectModal;
    modal.show(copy);
    return;
  }

  /**
   * ### 宛先モーダルの情報を更新する
   */
  addSendGroupTarget(targets: GroupModel): void {
    this.groups = targets;
    return;
  }

  /**
   * ### 更新確認モーダルを表示する
   *
   * - 表示前にバリデーションチェックして、エラーならモーダルは表示しない
   */
  public async showConfirmUpdateModal(): Promise<void> {
    this.showUpdateModal = true;
    return;
  }

  /**
   * ### 安否確認(施設関係者)テンプレートを更新する
   *
   * - リクエストボディを作成する
   * - 安否確認(施設関係者)テンプレートサービスを使用して更新する
   * - 更新成功ダイアログを表示する
   */
  public async doUpdate(): Promise<void> {
    this.isBusy = true;
    this.loadingMessage = "更新中...";

    try {
      const requestBody: OtherUserSafetyConfirmsTemplatesPatchModel = this.createRequestBody();
      await this.otherUserSafetyConfirmsTemplatesService.patch(userStore, 1, requestBody);

      this.showUpdateSuccessModal = true;
    } catch (error) {
      // ステータスコードが409の場合、他端末にて更新が行われているので、画面情報を再取得してから再入力を促す
      const systemError: SystemError = error as SystemError;
      if (systemError.statusCode == 409) {
        await this.createView();
        this.errorMessage = "他の端末で更新されたか、表示している情報が古いために更新できませんでした。情報を再読み込みして、もう一度お試しください。";
      }
      // その他のステータスコードの場合は上位に例外を投げなおす
      else {
        throw error;
      }
    } finally {
      this.isBusy = false;
    }

    return;
  }

  /**
   * ### 施設更新パラメータ作成
   *
   * - useIdsは「使用する」の質問内容のみにする
   */
  public createRequestBody(): OtherUserSafetyConfirmsTemplatesPatchModel {
    let useIds: Array<number> = [];
    for (const useId of this.confirms) {
      if (useId.use == true) {
        useIds.push(useId.id);
      }
    }

    const targets: string[] = [];
    for (const group of this.groups.otherUserGroupMemberGroups) {
      if (group.isTarget) {
        targets.push(String(group.id));
      }
    }
    for (const group of this.groups.otherUserGroups) {
      if (group.isTarget) {
        targets.push(String(group.otherUserGroupsSortKey));
      }
    }

    const params: OtherUserSafetyConfirmsTemplatesPatchModel = {
      useIds: useIds,
      targets: targets,
      openingSentence: this.openingSentence ? this.openingSentence : null,
      updated: this.template ? this.template.updated : 0
    };

    return params;
  }

  /** ### プレビューを表示する */
  public previewConfirm(): void {
    this.showPreviewModal = true;
    return;
  }

  /**
   * ### 安否確認一覧画面に遷移する
   */
  public gotoSafetyConfirmList(): void {
    this.$router.push(PcUrl.SafetyConfirmList);

    return;
  }
}

export interface UseId extends OtherUserSafetyConfirmMastersGetModel {
  /** 使用するかしないか */
  use: boolean;
  /** ラジオボタンの場合の選択肢 */
  choicesLabel: string;
}
</script>

<style lang="scss" scoped>
@import "../../../styles/pc/_colors.scss";

// 親要素からネストしてスタイルを指定する
#other-user-safety-confirm-template-edit {
  max-width: 820px;
  margin-left: auto;
  margin-right: auto;

  .note {
    margin: 0 0 8px 11px;
  }

  .message {
    margin-bottom: 15px;
  }

  .confirm-list {
    width: 98%;

    .questions {
      font-size: 18px;
      font-weight: bold;
      margin: 0 0 4px 0;
    }

    .content {
      width: 100%;
      padding-top: 5px;
      padding-bottom: 5px;
      padding-left: 11px;
      padding-right: 11px;
      margin-bottom: 15px;
      border-bottom: solid 1px #cccccc;
      .confirm {
        .title {
          font-size: 18px;
          font-weight: bold;
        }
        .text {
          font-size: 14px;
        }
      }
      // ON/OFFスイッチ
      .buttons {
        width: 130px;
      }
    }
    .content:last-child {
      border-bottom: 0;
      margin-bottom: 0;
    }
  }
  .update-btn,
  .cancel-btn {
    width: 120px;
  }
}
</style>
